import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	model,
	OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ComponentsModule } from '@components/components.module';
import {
	ClientSubType,
	EmailAttachment,
	EmailRecipientType,
} from '@models/global-email-templates';
import { MatterFileType } from '@models/matter';
import { dispatch, select } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import {
	SelectEmailTemplate,
	UpdateEmailTemplate,
} from '@state/email-templates/email-templates.actions';
import { ButtonComponent } from '@ui/button/button.component';
import { CheckboxComponent } from '@ui/checkbox/checkbox.component';
import { ContentHeaderComponent } from '@ui/content-header/content-header.component';
import { SectionComponent } from '@ui/section/section.component';
import { SelectComponent } from '@ui/select/select.component';
import { ToastService } from '@ui/toast/toast.service';
import { take } from 'rxjs';
import { EmailTemplateEditorComponent } from '../../../../../shared/components/email-template-editor/email-template-editor.component';
import {
	emailRecipientTypeOptions,
	fileTypeOptions,
} from '../email-templates.consts';
import { emailTemplateViewModel } from './email-template.selectors';
import { JurisdictionDepartments } from '@models/jurisdictions';

@Component({
	selector: 'app-email-template',
	templateUrl: './email-template.component.html',
	imports: [
		ContentHeaderComponent,
		SectionComponent,
		ButtonComponent,
		SelectComponent,
		CheckboxComponent,
		FormsModule,
		EmailTemplateEditorComponent,
		ComponentsModule,
		CommonModule,
	],
	standalone: true,
})
export class EmailTemplateComponent implements OnInit {
	navService = inject(NavigationService);
	route = inject(ActivatedRoute);
	toastService = inject(ToastService);

	viewModel_ = select(emailTemplateViewModel);
	selectEmailTemplate = dispatch(SelectEmailTemplate);
	updateEmailTemplate = dispatch(UpdateEmailTemplate);

	templateName_ = model('');
	fileType_ = model<MatterFileType>(null);
	default_ = model(false);
	emailRecipientType_ = model<EmailRecipientType>(null);
	emailRecipientSubType_ = model<ClientSubType | JurisdictionDepartments>(null);
	subject_ = model('');
	htmlMessage_ = model('');
	attachments_ = model<EmailAttachment[]>([]);

	EMAIL_RECIPIENT_TYPE_OPTIONS = emailRecipientTypeOptions;
	FILE_TYPE_OPTIONS = fileTypeOptions;

	emailRecipientSubTypeOptions_ = computed(() => {
		const option = this.EMAIL_RECIPIENT_TYPE_OPTIONS.find(
			(opt) => opt.id === this.emailRecipientType_(),
		);

		return option && 'subTypes' in option ? option.subTypes : null;
	});

	constructor() {
		effect(() => {
			const template = this.viewModel_().template;
			if (!template) return;

			const metadata = template.metadata;
			const version = template.version;
			if (metadata && version) {
				this.templateName_.set(metadata.templateName);
				this.fileType_.set(metadata.fileType);
				this.default_.set(metadata.default);
				this.emailRecipientType_.set(metadata.emailRecipientType);
				this.emailRecipientSubType_.set(metadata.emailRecipientSubType);
				this.subject_.set(version.subject);
				this.htmlMessage_.set(version.htmlMessage);
				this.attachments_.set(version.attachments);
			}
		});

		effect(() => {
			const recipientType = this.emailRecipientType_();
			const subTypeOptions = this.emailRecipientSubTypeOptions_();

			if (recipientType && subTypeOptions?.length > 0) {
				const currentSubType = this.emailRecipientSubType_();
				if (
					!currentSubType ||
					!subTypeOptions.some((opt) => opt.id === currentSubType)
				) {
					this.emailRecipientSubType_.set(subTypeOptions[0].id);
				}
			} else {
				this.emailRecipientSubType_.set(null);
			}
		});
	}

	ngOnInit() {
		const templateId = this.route.snapshot.params['templateId'];
		this.selectEmailTemplate(templateId);
	}

	onContentChange(content: string) {
		this.htmlMessage_.set(content);
	}

	saveTemplate() {
		const templateId = this.route.snapshot.params['templateId'];
		if (templateId) {
			const metadata = {
				templateName: this.templateName_(),
				fileType: this.fileType_(),
				default: this.default_(),
				emailRecipientType: this.emailRecipientType_(),
				emailRecipientSubType: this.emailRecipientSubType_(),
			};

			const version = {
				subject: this.subject_(),
				htmlMessage: this.htmlMessage_(),
				attachments: this.attachments_(),
			};

			this.updateEmailTemplate(templateId, metadata, version)
				.pipe(take(1))
				.subscribe(() => {
					this.toastService.success('Template saved successfully');
				});
		}
	}

	goBack() {
		this.navService.goToBusinessManageEmailTemplates();
	}
}
